import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ChangePasswordForm from '@/components/ChangePasswordForm';
import {
  AtSign, Mail, KeyRound, Calendar, MessageSquare, FileText, LogOut, Loader2, Briefcase, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Workspace dashboard for CRESA employees logged in with their @cresa-asbl.org address.
 * - Visible at /espace-employe
 * - Requires authentication; non-cresa accounts are redirected.
 */
export default function EmployeeWorkspacePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const isCresaEmployee = user?.email?.toLowerCase().endsWith('@cresa-asbl.org');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: '/espace-employe' } });
    }
  }, [loading, user, navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Déconnecté');
      navigate('/');
    } catch (e) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#4A6B48]" size={36} />
      </div>
    );
  }

  if (!user) return null;

  // Compose webmail provider link from local email
  const username = user.email.split('@')[0];
  const webmailLinks = [
    {
      name: 'Webmail LWS (recommandé)',
      url: 'https://webmail.cresa-asbl.org',
      hint: 'Accès direct à votre boîte hébergée chez LWS',
      primary: true,
    },
    {
      name: 'Webmail LWS — accès secours',
      url: 'https://webmail.lws.fr',
      hint: 'URL alternative si le webmail principal est indisponible',
    },
    { name: 'Gmail (Google Workspace)', url: `https://mail.google.com/a/cresa-asbl.org`, hint: 'Si CRESA migre un jour vers Google Workspace' },
    { name: 'Zoho Mail', url: `https://mail.zoho.com/`, hint: 'Si CRESA migre un jour vers Zoho Mail' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <section className="section-padding bg-[#F9F8F6]" data-testid="employee-workspace">
        <div className="container mx-auto px-6 max-w-5xl">
          {/* Header */}
          <div className="card flex items-center justify-between gap-4 mb-6 flex-wrap" data-testid="employee-header">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#4A6B48] text-white inline-flex items-center justify-center font-bold text-xl">
                {(user.name || username).charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Bienvenue, {user.name || username}
                </h1>
                <p className="text-sm text-[#5C665A] inline-flex items-center gap-1.5 font-mono">
                  <AtSign size={13} /> {user.email}
                </p>
              </div>
            </div>
            <button onClick={handleLogout} data-testid="employee-logout"
              className="inline-flex items-center gap-1.5 text-sm font-bold border-2 border-[#5C665A] text-[#5C665A] px-3 py-2 rounded hover:bg-[#5C665A] hover:text-white">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>

          {!isCresaEmployee && (
            <div className="card border-l-4 border-[#D87D4A] bg-[#D87D4A]/10 mb-6">
              <p className="text-sm text-[#2E332D]">
                <strong>Information :</strong> Cet espace est destiné aux employés disposant d&apos;une
                adresse <code>@cresa-asbl.org</code>. Vous pouvez tout de même utiliser les sections
                ci-dessous, mais l&apos;accès e-mail ne fonctionnera qu&apos;avec un compte CRESA officiel.
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-5">
            {[
              { id: 'overview', label: "Vue d'ensemble", icon: Briefcase },
              { id: 'webmail', label: 'Ma messagerie', icon: Mail },
              { id: 'security', label: 'Sécurité', icon: KeyRound },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`employee-tab-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold border-2 transition-all ${
                    tab === t.id
                      ? 'bg-[#4A6B48] border-[#4A6B48] text-white'
                      : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
                  }`}>
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {tab === 'overview' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employee-overview">
              <a href="/blog" className="card hover:-translate-y-0.5 transition-transform group">
                <FileText className="text-[#4A6B48] mb-2 group-hover:scale-110 transition-transform" size={28} />
                <h3 className="font-bold text-[#2E332D]">Blog & Articles</h3>
                <p className="text-xs text-[#5C665A] mt-1">Consulter et partager les articles CRESA</p>
              </a>
              <a href="/calendrier" className="card hover:-translate-y-0.5 transition-transform group">
                <Calendar className="text-[#4A6B48] mb-2 group-hover:scale-110 transition-transform" size={28} />
                <h3 className="font-bold text-[#2E332D]">Calendrier</h3>
                <p className="text-xs text-[#5C665A] mt-1">Activités et événements à venir</p>
              </a>
              <a href="/documents" className="card hover:-translate-y-0.5 transition-transform group">
                <FileText className="text-[#4A6B48] mb-2 group-hover:scale-110 transition-transform" size={28} />
                <h3 className="font-bold text-[#2E332D]">Documents</h3>
                <p className="text-xs text-[#5C665A] mt-1">Rapports, études, publications</p>
              </a>
              <button onClick={() => setTab('webmail')} className="card hover:-translate-y-0.5 transition-transform group text-left">
                <Mail className="text-[#D87D4A] mb-2 group-hover:scale-110 transition-transform" size={28} />
                <h3 className="font-bold text-[#2E332D]">Ma messagerie CRESA</h3>
                <p className="text-xs text-[#5C665A] mt-1">{user.email}</p>
              </button>
              <a href="/contact" className="card hover:-translate-y-0.5 transition-transform group">
                <MessageSquare className="text-[#4A6B48] mb-2 group-hover:scale-110 transition-transform" size={28} />
                <h3 className="font-bold text-[#2E332D]">Contact / Support</h3>
                <p className="text-xs text-[#5C665A] mt-1">Joindre l&apos;administration</p>
              </a>
              <button onClick={() => setTab('security')} className="card hover:-translate-y-0.5 transition-transform group text-left">
                <KeyRound className="text-[#4A6B48] mb-2 group-hover:scale-110 transition-transform" size={28} />
                <h3 className="font-bold text-[#2E332D]">Mon mot de passe</h3>
                <p className="text-xs text-[#5C665A] mt-1">Changer mon mot de passe de connexion</p>
              </button>
            </div>
          )}

          {tab === 'webmail' && (
            <div className="card" data-testid="employee-webmail">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#D87D4A]/10 inline-flex items-center justify-center">
                  <Mail className="text-[#D87D4A]" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Accéder à votre boîte e-mail CRESA
                  </h2>
                  <p className="text-sm text-[#5C665A]">
                    Votre adresse : <strong className="font-mono">{user.email}</strong>
                  </p>
                </div>
              </div>

              <p className="text-sm text-[#5C665A] leading-relaxed mb-4">
                Cliquez sur le bouton correspondant au fournisseur de messagerie configuré pour
                votre domaine <code>@cresa-asbl.org</code>. Vous serez redirigé(e) vers la page de
                connexion officielle pour consulter, envoyer et gérer vos e-mails.
              </p>

              <div className="space-y-3">
                {webmailLinks.map((w) => (
                  <a key={w.name} href={w.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`webmail-${w.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    className={`flex items-center justify-between gap-3 p-4 border-2 rounded-lg transition-all group ${
                      w.primary
                        ? 'border-[#D87D4A] bg-[#D87D4A]/5 hover:bg-[#D87D4A] hover:text-white'
                        : 'border-[#E5E3DB] hover:border-[#4A6B48] hover:bg-[#4A6B48]/5'
                    }`}>
                    <div>
                      <h3 className={`font-bold inline-flex items-center gap-2 ${w.primary ? 'text-[#D87D4A] group-hover:text-white' : 'text-[#2E332D]'}`}>
                        <Mail size={16} className={w.primary ? 'text-[#D87D4A] group-hover:text-white' : 'text-[#4A6B48]'} />
                        {w.name}
                        {w.primary && <span className="text-[10px] bg-[#D87D4A] text-white px-2 py-0.5 rounded-full font-bold group-hover:bg-white group-hover:text-[#D87D4A]">LWS</span>}
                      </h3>
                      <p className={`text-xs mt-1 ${w.primary ? 'text-[#5C665A] group-hover:text-white/90' : 'text-[#5C665A]'}`}>{w.hint}</p>
                    </div>
                    <ExternalLink size={18} className={w.primary ? 'text-[#D87D4A] group-hover:text-white' : 'text-[#5C665A] group-hover:text-[#4A6B48]'} />
                  </a>
                ))}
              </div>

              {/* IMAP / SMTP configuration for desktop / mobile clients */}
              <div className="mt-6 p-4 bg-[#F9F8F6] rounded border border-[#E5E3DB]">
                <h4 className="text-sm font-bold text-[#2E332D] mb-2 inline-flex items-center gap-1.5">
                  ⚙️ Configurer Outlook / Apple Mail / Thunderbird (LWS)
                </h4>
                <p className="text-xs text-[#5C665A] mb-3">
                  Pour configurer votre adresse <strong>{user.email}</strong> dans un client de messagerie
                  de bureau ou mobile, utilisez les paramètres ci-dessous :
                </p>
                <div className="grid sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-white p-3 rounded border border-[#E5E3DB]">
                    <p className="font-bold text-[#4A6B48] mb-1">📥 Réception (IMAP)</p>
                    <ul className="space-y-0.5 text-[#2E332D] font-mono">
                      <li>Serveur : <strong>mail.cresa-asbl.org</strong></li>
                      <li>Port : <strong>993</strong> (SSL/TLS)</li>
                      <li>Identifiant : <strong>{user.email}</strong></li>
                    </ul>
                  </div>
                  <div className="bg-white p-3 rounded border border-[#E5E3DB]">
                    <p className="font-bold text-[#4A6B48] mb-1">📤 Envoi (SMTP)</p>
                    <ul className="space-y-0.5 text-[#2E332D] font-mono">
                      <li>Serveur : <strong>mail.cresa-asbl.org</strong></li>
                      <li>Port : <strong>465</strong> (SSL/TLS)</li>
                      <li>Identifiant : <strong>{user.email}</strong></li>
                    </ul>
                  </div>
                </div>
                <p className="text-[11px] text-[#5C665A] mt-3 italic">
                  Mot de passe : celui de votre compte CRESA. Authentification requise pour SMTP.
                </p>
              </div>

              <div className="mt-6 p-4 bg-[#F9F8F6] rounded border border-[#E5E3DB] text-xs text-[#5C665A]">
                <strong>Informations utiles :</strong>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li>Identifiant : <code className="font-mono">{user.email}</code></li>
                  <li>Mot de passe : celui qui vous a été communiqué lors de l&apos;approbation de votre demande.</li>
                  <li>En cas d&apos;oubli ou de problème, demandez à l&apos;administration CRESA de réinitialiser votre mot de passe.</li>
                </ul>
              </div>
            </div>
          )}

          {tab === 'security' && (
            <div className="max-w-xl" data-testid="employee-security">
              <ChangePasswordForm />
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
